import { useMemo } from "react"
import type { PreOrder } from "@stockline/shared/types/orders/pre-order"
import { PRE_ORDER_TABLE } from "../../../lib/offline/resources/orders/pre-order/db"
import { useAISync } from "../../ai/providers/AISyncProvider"

export interface UsePreOrdersFilter {
  status?: number
  partnerId?: string
  whatsappChatId?: string
  emailId?: string
  smsChatId?: string
  /**
   * If true, include soft-deleted rows. Defaults to false — recap and chat
   * surfaces never want tombstones since the SSE delete event is what removes
   * the card; including deleted would re-render zombies until the next pull.
   */
  includeDeleted?: boolean
}

export function usePreOrders(filter?: UsePreOrdersFilter): PreOrder[] {
  const { reactive } = useAISync()
  const rows = reactive.useQuery<PreOrder>(PRE_ORDER_TABLE, {
    ...(filter?.status !== undefined ? { status: filter.status } : {}),
    ...(filter?.partnerId ? { partnerId: filter.partnerId } : {}),
    ...(filter?.whatsappChatId
      ? { whatsappChatId: filter.whatsappChatId }
      : {}),
    ...(filter?.emailId ? { emailId: filter.emailId } : {}),
    ...(filter?.smsChatId ? { smsChatId: filter.smsChatId } : {}),
    orderBy: "-createdAt",
  })
  // Memo the tombstone filter so consumers' useMemo dep checks see a stable
  // reference across renders that don't actually change underlying data.
  // useLiveQuery returns a stable reference between Dexie ticks; without this
  // memo, .filter() recreates the array on every parent re-render and any
  // downstream useMemo keyed on the result re-runs even when nothing changed.
  const includeDeleted = filter?.includeDeleted
  return useMemo(
    () => (includeDeleted ? rows : rows.filter((r) => !r.deletedAt)),
    [rows, includeDeleted]
  )
}
